import React, { useCallback } from "react";
import IntlMessageFormat from "intl-messageformat";
import * as R from "ramda";

import translations from "translations";
import Markdown from "components/Markdown";
import { useCurrentTenant } from "contexts/currentTenant";
import { useLocale } from "contexts/locale";
import { getAccelaACAName, getFullName, getMunicipalityType, getName } from "reducers/tenant";
import { reportError } from "services/errorReporter";
import { isBlank } from "utils/func";

function htmlDecode(input) {
  const doc = new DOMParser().parseFromString(input, "text/html");
  return doc.documentElement.textContent;
}

const lookupTranslation = (locale) => (key) => R.path([locale, ...key.split(".")])(translations);

const Text = ({ t, markdown = false, className = undefined, ...interpolationValues }) => {
  let str = useText(t, interpolationValues);
  if (markdown) str = <Markdown source={str} />;

  return (
    <span data-i18n-key={t} className={className}>
      {str}
    </span>
  );
};
export default Text;

export const useGetText = () => {
  const locale = useLocale();
  const lookup = lookupTranslation(locale);
  const tenant = useCurrentTenant();

  const defaultValues = R.applySpec({
    tenantName: getName,
    municipalityType: R.compose(R.unless(isBlank, R.toLower), getMunicipalityType),
    tenantFullName: getFullName,
    aca: getAccelaACAName,
  })(tenant);

  return useCallback(
    (key, values = {}) => {
      const rawText = lookup(key);
      if (!rawText) {
        reportError(new Error(`Missing translation: ${key}`), { params: { locale, key } });
        return key;
      }
      const valuesWithDefaults = R.mergeRight(defaultValues, values);

      try {
        // FIXME: parsing this each time seems highly wasteful
        const message = new IntlMessageFormat(rawText, locale);
        return htmlDecode(message.format(valuesWithDefaults));
      } catch (e) {
        reportError(e, { params: { locale, key } });
        return key;
      }
    },
    [locale, defaultValues, lookup],
  );
};

export const useText = (key, values = {}) => {
  const getText = useGetText();
  return getText(key, values);
};
